import '@fontsource/inter';
import { AxiosError } from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, Calendar } from 'react-feather';
import { useMediaQuery } from 'react-responsive';
import { useHistory, useLocation } from 'react-router-dom';
import { Api, queryGpt3, retrieveSubscription, upsertJournalEntry } from '../../api';
import { loadExistingEntry } from '../../components/calendar/calendar';
import {
  EntryBlock,
  EntryBlockWithContext,
  EntryStates,
  S3Entry,
  isEntryStates
} from '../../components/calendar/types';
import TagFilter from '../../components/TagFilter';
import { useAuthContext, useEntryContext, useGeneralContext } from '../../context';
import {
  browserTimestampJournalEntryFormat,
  daysAgo,
  getDayMonthYearFromDate,
  getYearMonthDayNumbersFromCreationDateString,
  sameDay
} from '../../util/dateTime';
import { GptPromptCard } from './GptPromptCard';
import Navigation from '../../components/Nav/Nav';
import PremiumUpsellModal from '../../components/PremiumUpsellModal';
import { userIsFree } from '../../util/subscription';
import { CELL_SIZE_PX, CellSize, GridLegend, WEEKDAY_GUTTER_PX, WeeklyColumnsGrid } from '../../components/grid/Grid';
import { useWeeksThatFit } from '../../components/grid/useWeeksThatFit';
import { above } from '../../breakpoints';
import JumbleModal from '../../components/jumbleModal/JumbleModal';
import { StartSomethingNew, TodayCard } from './TodayCard';
import { RecentEntries, RecentEntry } from './RecentEntries';
import { JournalPreviews, Theme } from './JournalPreviews';
import { CATEGORY_LABELS } from '../retrospective/types';
import { DEFAULT_ASSISTANT } from '../entry/types';

/** How many recent entries the "Recently" list shows, and how many we fetch. */
const RECENT_COUNT = 5;

/*
 * Cap at roughly six months of week columns. A full year of 16px cells
 * doesn't fit most desktop widths without horizontal scrolling, which
 * defeats the point of an at-a-glance grid; six months reliably fits.
 */
const MAX_WEEKS = 26;

const SUGGESTED_QUESTIONS = [
  'What was I worried about this time last year?',
  'When do I write about feeling most like myself?',
  'Have I made the same decision twice?'
];

/** Tag themes have no colour of their own, so they borrow the brand ramp. */
const TAG_THEME_COLORS = ['#7543e8', '#9169ed', '#ac8ef1', '#e84364'];

type LoadedEntry = { name: string; imported: boolean; tags: string[]; blocks: EntryBlock[] };

const Home = () => {
  const context = useGeneralContext();
  const EntryContext = useEntryContext();
  const Auth = useAuthContext();

  const { signOut, user } = Auth;
  const history = useHistory();
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [isLoadingEntries, setIsLoadingEntries] = useState<boolean>(true);
  const [openingEntry, setOpeningEntry] = useState<boolean>(false);
  const [gptPrompt, setGptPrompt] = useState<EntryBlock[]>([]);
  const [upsellMessage, setUpsellMessage] = useState<string | null>(null);
  const [showPromptCard, setShowPromptCard] = useState(false);
  const initConversation: EntryBlockWithContext[] = [
    { context: [], role: 'assistant', content: "Hi there, I'm Jumble" },
    { context: [], role: 'assistant', content: 'I can generate prompts to help you write' },
    { context: [], role: 'assistant', content: 'What topic would you like to write about today?' }
  ];
  const [conversation, setConversation] = useState<EntryBlockWithContext[]>(initConversation);
  const [tagsByEntry, setTagsByEntry] = useState<
    {
      entryName: string;
      tags: string[];
    }[]
  >([]);
  const { nickname, setAllTags, subscriptionStatus, setSubscriptionStatus, jwtIsSet, nuggets, importationEmail } =
    context;
  const { entries, setEntries, addEntry } = EntryContext;

  // The contents of the most recent few entries, which feed both the today card
  // and the "Recently" list. One shared fetch, and the loaded state is handed to
  // the entry page on click so opening a row costs nothing.
  const [loadedEntries, setLoadedEntries] = useState<LoadedEntry[]>([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(true);

  // A year of 16px cells fits a desktop content column; a phone gets touch-sized
  // cells and however many weeks the card is wide enough for.
  const isDesktop = useMediaQuery(above('lg'));
  const cellSize: CellSize = isDesktop ? 'sm' : 'md';
  const [gridContainer, setGridContainer] = useState<HTMLDivElement | null>(null);
  const weeksToDisplay = useWeeksThatFit(gridContainer, {
    cellPx: CELL_SIZE_PX[cellSize],
    gutterPx: WEEKDAY_GUTTER_PX,
    maxWeeks: MAX_WEEKS
  });
  const [range, setRange] = useState({
    start: getDayMonthYearFromDate(daysAgo(7 * weeksToDisplay)),
    end: getDayMonthYearFromDate(new Date())
  });

  // Widening the window shows more weeks, so the range has to grow with it.
  useEffect(() => {
    setRange({
      start: getDayMonthYearFromDate(daysAgo(7 * weeksToDisplay)),
      end: getDayMonthYearFromDate(new Date())
    });
  }, [weeksToDisplay]);

  const location = useLocation();

  if (!user.getSignInUserSession()) {
    window.alert('Your session has expired. Please log back in.');
    signOut();
  }

  // refresh subscription
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const refreshSub = async () => {
      try {
        const refreshSub = queryParams.get('refreshSub') === 'true';
        if (!refreshSub) {
          return;
        }
        const userSub = await retrieveSubscription();
        if (userSub?.data?.stripeCustomerId) {
          sessionStorage.setItem('stripeCustomerId', userSub?.data?.stripeCustomerId);
        }
        setSubscriptionStatus(userSub?.data?.status || null);
      } catch (err) {
        // TODO send error to backend error handler
      }
    };
    if (!jwtIsSet) {
      return;
    }
    refreshSub();
  }, [setSubscriptionStatus, location.search, jwtIsSet]);

  const dateFilter = new Date();

  const existingEntryToday = entries.find((entry) => sameDay(dateFilter, entry.name));

  // set entries
  // TODO move this into the context provider
  useEffect(() => {
    let isCanceled = false;
    const controller = new AbortController();
    const retrieveAndSetJournalEntries = async () => {
      try {
        !isCanceled && setIsLoadingEntries(true);
        const response = await Api.post('retrieveJournalEntries', {
          signal: controller.signal
        });
        if (response.data.entries.length === 0) {
          !isCanceled && setIsLoadingEntries(false);
          setEntries([]);
          setAllTags([]);
          return;
        }

        const incomingTags = response.data.tags || [];
        const s3Entries: S3Entry[] = response.data.entries;

        if (!isCanceled) {
          setAllTags(incomingTags);

          setTagsByEntry(s3Entries.map((entry) => ({ entryName: entry.entryName, tags: entry.tags })));
          const newEntries = s3Entries.map((entry) => ({ name: entry.entryName, imported: entry.imported || false }));

          setEntries(newEntries);
          setIsLoadingEntries(false);
        }
      } catch (err: any) {
        if (err instanceof AxiosError && err?.response?.status === 401) {
          return;
        }
        if (err.code === 'ERR_CANCELED') {
          return;
        }
        console.error(err);
        setIsLoadingEntries(false);
      }
    };
    if (jwtIsSet) {
      retrieveAndSetJournalEntries();
    }
    return () => {
      isCanceled = true;
      controller.abort();
    };
  }, [setAllTags, setEntries, jwtIsSet]);

  /*
   * Entry *contents* are one S3 read each, so only the handful of entries the
   * page actually shows text for get loaded. `retrieveJournalEntries` returns
   * names and tags only — there is no bulk content or word-count endpoint.
   */
  const recentEntryNames = useMemo(
    () =>
      [...entries]
        .sort((a, b) => b.name.localeCompare(a.name))
        .slice(0, RECENT_COUNT)
        .map((entry) => `${entry.name}:${entry.imported}`)
        .join(','),
    [entries]
  );

  useEffect(() => {
    let isCanceled = false;
    const loadRecentEntries = async () => {
      if (!recentEntryNames) {
        setLoadedEntries([]);
        setIsLoadingRecent(false);
        return;
      }
      setIsLoadingRecent(true);
      try {
        const loaded = await Promise.all(
          recentEntryNames.split(',').map(async (pair) => {
            const [name, imported] = pair.split(':');
            const entry = await loadExistingEntry({ entryName: name });
            return { name, imported: imported === 'true', tags: entry.tags || [], blocks: entry.blocks };
          })
        );
        if (!isCanceled) {
          setLoadedEntries(loaded);
        }
      } catch (err) {
        // The page is still usable without excerpts, so this failure is quiet.
        console.error(err);
        if (!isCanceled) {
          setLoadedEntries([]);
        }
      }
      if (!isCanceled) {
        setIsLoadingRecent(false);
      }
    };
    if (jwtIsSet && !isLoadingEntries) {
      loadRecentEntries();
    }
    return () => {
      isCanceled = true;
    };
  }, [recentEntryNames, isLoadingEntries, jwtIsSet]);

  const navigateToDailyEntry = async () => {
    const { entry } = getEntryOnDate(entries, new Date());

    if (openingEntry) {
      return;
    }
    setOpeningEntry(true);
    if (entry) {
      try {
        let singleEntryState = [];
        for (const entry of entries) {
          if (!sameDay(new Date(), entry.name)) {
            continue;
          }
          const le = await loadExistingEntry({
            entryName: entry.name,
            setOpeningEntry
          });

          singleEntryState.push({
            ...le,
            name: entry.name
          });
        }

        if (gptPrompt.length > 0) {
          singleEntryState[singleEntryState.length - 1].blocks.push(...gptPrompt);
          singleEntryState[singleEntryState.length - 1].blocks.push({ role: 'user', content: '' });
        }

        if (!isEntryStates(singleEntryState)) {
          const error = `[navigateToDailyEntry] Existing Entry Invalid entryState: ${JSON.stringify(singleEntryState)}`;
          throw new Error(error);
        }

        setOpeningEntry(false);
        return history.push({
          pathname: `/entry/${entry.name}`,
          state: {
            entryState: singleEntryState
          }
        });
      } catch (err) {
        setOpeningEntry(false);
        window.alert(
          'There was an issue retrieving Journal Entry. Make sure you have internet and try again. If the issue persists, let us know at support@jumblejournal.org'
        );
        return;
      }
    }

    // entry does not exist
    return createEntryOnDate(new Date());
  };

  /*
   * Writing an entry for a day other than today needs no new backend: an entry
   * is identified by the timestamp in its name, so a past day is a past
   * timestamp. Noon keeps it clear of either end of the day in any timezone.
   */
  const createEntryOnDate = async (date: Date) => {
    const creationDateTitle = browserTimestampJournalEntryFormat(
      new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0)
    );
    const entryData: EntryStates = [
      {
        blocks: [...gptPrompt, { role: 'user', content: '' }],
        imported: false,
        name: creationDateTitle,
        tags: []
      }
    ];

    if (!isEntryStates(entryData)) {
      const error = `[createEntryOnDate] New Entry Invalid entryData: ${JSON.stringify(entryData)}`;
      throw new Error(error);
    }

    try {
      // The entry page stores one { blocks, imported } object per entry and the
      // reader expects that shape, so a page created here has to match it —
      // storing the whole array made the loaded entry one block of raw JSON.
      await upsertJournalEntry({
        value: JSON.stringify({ blocks: entryData[0].blocks, imported: false }),
        creationDateTitle,
        embedEntry: false
      });
    } catch (error: any) {
      if (error instanceof AxiosError && error?.response?.status === 404) {
        window.alert('Unable to save because you have been logged out. Please log back in.');
      }
    }

    addEntry(creationDateTitle, false);

    return history.push({
      pathname: `/entry/${creationDateTitle}`,
      state: { entryState: entryData }
    });
  };

  const openEmptyDay = async (date: Date) => {
    if (openingEntry) {
      return;
    }
    setOpeningEntry(true);
    try {
      await createEntryOnDate(date);
    } catch (err) {
      setOpeningEntry(false);
      window.alert(
        'There was an issue creating the entry. Make sure you have internet and try again. If the issue persists, let us know at support@jumblejournal.org'
      );
    }
  };

  /*
   * The entry page builds itself from the state it is pushed with — it does not
   * fetch by name — so a row can only be opened with its loaded contents.
   */
  const openLoadedEntry = (entryName: string) => {
    const loaded = loadedEntries.find((entry) => entry.name === entryName);
    if (!loaded) {
      return;
    }
    const entryState = [{ blocks: loaded.blocks, imported: loaded.imported, name: loaded.name, tags: loaded.tags }];
    if (!isEntryStates(entryState)) {
      return;
    }
    return history.push({ pathname: `/entry/${entryName}`, state: { entryState } });
  };

  const todayEntries = loadedEntries.filter((entry) => sameDay(dateFilter, entry.name));
  const todayText = todayEntries
    .map((entry) => entryText(entry.blocks))
    .join(' ')
    .trim();
  const recent: RecentEntry[] = loadedEntries.map((entry) => ({
    name: entry.name,
    imported: entry.imported,
    tags: entry.tags,
    text: entryText(entry.blocks)
  }));

  const mentorName = localStorage.getItem('lastMentor') || DEFAULT_ASSISTANT;
  const isFree = userIsFree(subscriptionStatus);

  const themes = useMemo(() => buildThemes(nuggets, tagsByEntry), [nuggets, tagsByEntry]);

  const streak = calculateStreak(entries);
  const longestStreak = calculateLongestStreak(entries);
  const entriesThisYear = entries.filter(
    (entry) => getYearMonthDayNumbersFromCreationDateString(entry.name).year === new Date().getFullYear()
  ).length;
  const timeOfDay = new Date().getHours();

  return (
    <div className="tw-col-span-full tw-grid tw-h-full tw-grid-cols-[minmax(0,1fr)] lg:tw-grid-cols-[17rem_minmax(0,1fr)] tw-bg-[url('../../public/bg.png')] tw-bg-cover tw-bg-no-repeat tw-bg-center">
      <PremiumUpsellModal
        showModal={!!upsellMessage}
        setShowModal={() => setUpsellMessage(null)}
        message={upsellMessage || undefined}
      />
      <JumbleModal
        showModal={showPromptCard}
        closeModal={() => setShowPromptCard(false)}
        modalSections={[
          () => (
            <GptPromptCard
              navigateToDailyEntry={navigateToDailyEntry}
              isLoadingEntries={isLoadingEntries}
              conversation={conversation}
              clearConversation={() => {
                setConversation(initConversation);
                setGptPrompt([]);
              }}
              handleUserQuery={handleGptPromptQuery}
            />
          )
        ]}
      />
      <Navigation />
      <div className="tw-flex tw-flex-col tw-gap-10 tw-px-4 tw-pt-20 tw-pb-10 sm:tw-px-6 lg:tw-col-start-2 lg:tw-m-8 lg:tw-p-10 lg:tw-pt-10 lg:tw-gap-12 lg:tw-bg-white lg:tw-rounded-3xl lg:tw-self-stretch tw-min-w-0">
        {/* ------------------------------------------------------- today */}
        <section className="tw-flex tw-flex-col tw-gap-5 tw-min-w-0">
          <div className="tw-flex tw-flex-col tw-gap-1 md:tw-flex-row md:tw-items-end md:tw-justify-between">
            <h1 className="tw-m-0 tw-font-light tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-text-neutral-800">
              {timeOfDay < 12 ? 'Good morning' : timeOfDay < 16 ? 'Good afternoon' : 'Good evening'}
              {nickname ? `, ${nickname}` : ''}{' '}
              <span role="img" aria-label="waving hand">
                👋
              </span>
            </h1>
            <div className="tw-text-sm tw-text-neutral-500">
              {dateFilter.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          </div>

          <TodayCard
            excerpt={existingEntryToday ? truncate(todayText) : null}
            wordCount={countWords(todayText)}
            isLoadingEntries={isLoadingEntries || isLoadingRecent}
            openingEntry={openingEntry}
            onContinue={navigateToDailyEntry}
          />

          <StartSomethingNew
            mentorName={mentorName}
            showPremiumBadge={isFree}
            disabled={openingEntry}
            onBlankPage={() => history.push('/entry')}
            onPrompt={() => {
              if (isFree) {
                setUpsellMessage('Get access to personalized journaling prompts.');
                return;
              }
              setShowPromptCard(true);
            }}
            onMentor={() => {
              if (isFree) {
                setUpsellMessage(`Write alongside ${mentorName} and other mentors with Jumble Premium.`);
                return;
              }
              history.push(`/journal_with_${mentorName.replace(/ /g, '_')}`);
            }}
          />
        </section>

        {/* -------------------------------------------------- your year */}
        <section className="tw-flex tw-flex-col tw-gap-5 tw-min-w-0">
          <div className="tw-flex tw-flex-col tw-gap-3 md:tw-flex-row md:tw-items-center md:tw-justify-between">
            <h2 className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-font-light tw-text-xl lg:tw-text-2xl tw-text-neutral-800">
              <Calendar className="tw-w-6 tw-h-6 tw-shrink-0" />
              Your year
            </h2>
            <TagFilter tagFilter={tagFilter} setTagFilter={setTagFilter} dateRange={range} tagsByEntry={tagsByEntry} />
          </div>

          <div className="tw-rounded-3xl tw-border-2 tw-border-solid tw-border-neutral-100 tw-bg-white tw-p-5 lg:tw-p-6 tw-flex tw-flex-col tw-gap-4 tw-min-w-0">
            {isLoadingEntries ? (
              <div className="tw-flex tw-justify-center tw-py-16">
                <span
                  className="tw-h-16 tw-w-16 tw-inline-block tw-border-4 tw-border-primary-500 tw-border-t-transparent tw-rounded-full tw-animate-spin"
                  role="status"
                  aria-hidden="true"
                ></span>
              </div>
            ) : (
              <>
                <div ref={setGridContainer} className="tw-flex tw-flex-col tw-items-center tw-min-w-0 tw-overflow-x-auto">
                  <WeeklyColumnsGrid
                    tagFilter={tagFilter}
                    gridValues={tagsByEntry}
                    handleClick={() => {
                      setOpeningEntry(true);
                    }}
                    dateRangeSelectorOnTop={true}
                    range={range}
                    setRange={setRange}
                    weeksToDisplay={weeksToDisplay}
                    cellSize={cellSize}
                    showWeekdayLabels={true}
                    onEmptyDayClick={openEmptyDay}
                  />
                </div>
                <div className="tw-flex tw-flex-col tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-pt-4 md:tw-flex-row md:tw-items-center md:tw-justify-between">
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-sm tw-text-neutral-600">
                    <span className="tw-font-semibold tw-text-neutral-800">
                      <span role="img" aria-label="fire">
                        🔥
                      </span>{' '}
                      {streak}-day streak
                    </span>
                    <span className="tw-text-neutral-300">·</span>
                    <span>longest {longestStreak}</span>
                    <span className="tw-text-neutral-300">·</span>
                    <span>
                      {entriesThisYear} {entriesThisYear === 1 ? 'entry' : 'entries'} this year
                    </span>
                  </div>
                  <GridLegend />
                </div>
                <div className="tw-text-sm tw-text-neutral-500">
                  Tap any day to open it — or to write an entry for a day you missed.
                </div>
              </>
            )}
          </div>
        </section>

        {/* --------------------------------------------------- recently */}
        <section className="tw-flex tw-flex-col tw-gap-5">
          <h2 className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-font-light tw-text-xl lg:tw-text-2xl tw-text-neutral-800">
            <BookOpen className="tw-w-6 tw-h-6 tw-shrink-0" />
            Recently
          </h2>
          <RecentEntries
            recent={recent}
            isLoading={isLoadingEntries || isLoadingRecent}
            showImportNudge={!importationEmail}
            onOpen={openLoadedEntry}
          />
        </section>

        {/* ------------------------------------- explore and chat */}
        <JournalPreviews
          themes={themes}
          suggestedQuestions={SUGGESTED_QUESTIONS}
          onAsk={(question) => history.push({ pathname: '/retrospective', state: { initialQuery: question } })}
        />
      </div>
    </div>
  );

  async function handleGptPromptQuery(newConversation: EntryBlockWithContext[]) {
    if (userIsFree(subscriptionStatus)) {
      setShowPromptCard(false);
      setUpsellMessage('Get access to personalized journaling prompts.');
      return;
    }
    const userQuery = newConversation.slice(-1)[0].content;
    const result = await queryGpt3({ prompt: userQuery });
    if (!result || !result.data) {
      return;
    }

    const response = result.data?.gptResponse;

    if (!response) {
      window.alert(
        'There was an issue generating a journaling prompt. Please try again. Let us know if the issue persists: support@jumblejournal.org '
      );
      return;
    }

    setConversation([...newConversation, { context: [], role: 'assistant', content: response }]);
    setGptPrompt([
      ...newConversation.slice(initConversation.length),
      { context: [], role: 'assistant', content: response } as EntryBlockWithContext
    ]);
  }
};

/** Entry blocks are plain text, but imported entries can carry stray markup. */
const entryText = (blocks: EntryBlock[]) => {
  const written = blocks.filter((block) => block.role === 'user');
  return (written.length > 0 ? written : blocks)
    .map((block) => block.content)
    .join(' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const countWords = (text: string) => (text ? text.split(/\s+/).length : 0);

const truncate = (text: string, length = 160) => (text.length > length ? `${text.slice(0, length).trimEnd()}…` : text);

/*
 * Themes come from nuggets — the categorized excerpts the retrospective already
 * fetches into context — so this card costs no extra request. Users who have
 * not run a retrospective yet still have their own tags to show.
 */
const buildThemes = (nuggets: { category: string }[], tagsByEntry: { tags: string[] }[]): Theme[] => {
  const count = (keys: string[]) => {
    const counts = new Map<string, number>();
    keys.filter(Boolean).forEach((key) => counts.set(key, (counts.get(key) || 0) + 1));
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4);
  };

  if (nuggets.length > 0) {
    return count(nuggets.map((nugget) => nugget.category)).map(([key, total]) => {
      const category = CATEGORY_LABELS.find((candidate) => candidate.key === key);
      return { label: category?.label || key, count: total, color: category?.color || TAG_THEME_COLORS[0] };
    });
  }

  return count(tagsByEntry.flatMap((entry) => entry.tags)).map(([tag, total], i) => ({
    label: tag,
    count: total,
    color: TAG_THEME_COLORS[i % TAG_THEME_COLORS.length]
  }));
};

const getEntryOnDate = (entries: { name: string; imported: boolean }[], date: Date) => {
  const todayDate = getDayMonthYearFromDate(date);
  let index: number = -1;
  const entry = entries.find((entry, i) => {
    if (sameDay(todayDate, entry.name)) {
      index = i;
      return true;
    }
    return false;
  });
  return { entry, index };
};

const calculateStreak = (entries: { name: string; imported: boolean }[]) => {
  let streak = 0;
  const yesterday = daysAgo(streak + 1);
  let result = getEntryOnDate(entries, yesterday);
  while (result.entry !== undefined && result.index > -1) {
    streak++;
    let checkDate = daysAgo(streak + 1);
    result = getEntryOnDate(entries, checkDate);
  }
  // otherwise return streak (adding one if there is an entry today)
  const finalStreak = getEntryOnDate(entries, new Date()).entry ? streak + 1 : streak;
  return finalStreak;
};

/** The best run ever, which is the number worth chasing once a streak breaks. */
const calculateLongestStreak = (entries: { name: string }[]) => {
  const days = [...new Set(entries.map((entry) => entry.name.slice(0, 8)))].sort();
  let longest = 0;
  let current = 0;
  let previous: number | null = null;
  for (const day of days) {
    const { year, month, day: dayOfMonth } = getYearMonthDayNumbersFromCreationDateString(`${day}000000000`);
    const time = new Date(year, month, dayOfMonth).getTime();
    current = previous !== null && Math.round((time - previous) / 86400000) === 1 ? current + 1 : 1;
    longest = Math.max(longest, current);
    previous = time;
  }
  return longest;
};

export default Home;
